import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mountain, Landmark, ShieldCheck } from "lucide-react";

const TABS = [
  {
    id: "mines",
    label: "The Mines",
    icon: Mountain,
    title: "Nimba Range mines",
    body: "Our gold is won from workings across Liberia's mineral-rich Nimba region. We operate at the source — no brokers, no middlemen — which is how we can sell wholesale from $60,000 with a 500 g minimum.",
    image: "/photos/assay-bowl.jpg",
  },
  {
    id: "hq",
    label: "Refining & Sales HQ",
    icon: Landmark,
    title: "Monrovia headquarters",
    body: "Our sales headquarters in Monrovia, Liberia refines, stamps and assay-certifies every bar. Each bar is serial-numbered and sealed with documentation before it is released for sale.",
    image: "/photos/scale-weigh.jpg",
  },
  {
    id: "logistics",
    label: "Vault Logistics",
    icon: ShieldCheck,
    title: "Secure, insured dispatch",
    body: "From our Monrovia vaults, orders move under escrow-backed settlement and fully insured transit arrangements agreed with each wholesale buyer — from single pallets to multi-kilogram consignments.",
    image: "/photos/bars-case.jpg",
  },
];

const LiberiaStorySection = () => {
  const [tab, setTab] = useState("mines");
  const active = TABS.find((t) => t.id === tab);

  return (
    <section id="liberia" className="py-24 lg:py-32 bg-[#F3EFE6]">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-2xl mb-14"
        >
          <p className="text-xs font-medium tracking-[0.3em] uppercase text-[#8C6D27] mb-4">Origin — Liberia, West Africa</p>
          <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1A1816]">
            Mines and sales headquarters, <em className="gold-foil-text">both in Liberia.</em>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-10 items-stretch">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
            className="lg:col-span-7 relative overflow-hidden bg-[#141312] min-h-[320px] lg:min-h-[480px]"
          >
            <AnimatePresence mode="wait">
              <motion.img
                key={active.id}
                data-testid={`story-image-${active.id}`}
                src={active.image}
                alt={active.title}
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="absolute inset-0 h-full w-full object-cover"
              />
            </AnimatePresence>
            <div className="absolute inset-0 bg-gradient-to-t from-[#141312]/70 via-transparent to-transparent" />
            <div className="absolute bottom-0 inset-x-0 p-7 text-[#FAF8F5]">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">Traceable · Ethically sourced</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
            className="lg:col-span-5 flex flex-col"
          >
            <div className="flex flex-col gap-2">
              {TABS.map((t) => {
                const Icon = t.icon;
                const isActive = t.id === tab;
                return (
                  <button
                    key={t.id}
                    data-testid={`story-tab-${t.id}`}
                    onClick={() => setTab(t.id)}
                    className={`flex items-center gap-4 px-6 py-5 text-left border transition-all duration-500 ${
                      isActive
                        ? "bg-[#141312] text-[#FAF8F5] border-[#141312]"
                        : "bg-transparent border-[#E6E0D4] hover:border-[#C5A059]/60 text-[#1A1816]"
                    }`}
                  >
                    <Icon size={18} className={isActive ? "text-[#D4AF37]" : "text-[#8C6D27]"} />
                    <span className="text-[12px] font-medium tracking-[0.18em] uppercase">{t.label}</span>
                  </button>
                );
              })}
            </div>
            <AnimatePresence mode="wait">
              <motion.div
                key={active.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                className="mt-8"
              >
                <h3 className="font-serif text-2xl text-[#1A1816]">{active.title}</h3>
                <p className="mt-4 text-sm sm:text-base leading-relaxed text-[#6E6A63] font-light">{active.body}</p>
              </motion.div>
            </AnimatePresence>
            <div className="mt-auto pt-8 border-t border-[#E6E0D4]">
              <p className="text-xs leading-relaxed text-[#6E6A63]/80 font-light">
                Every Pacific Gold bar is serial-numbered and assay-certified at our Monrovia headquarters — origin
                you can verify, weight you can trust.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default LiberiaStorySection;
